/** Alerts, exports, report templates, promos, holidays, store users and API keys (TZ 8.9, 8.10, settings). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEndpoints } from '../api_context';
import type {
  AlertPreferencesUpdate,
  ApiKeyCreate,
  ExportRequest,
  PromoCreate,
  ReportTemplateCreate,
  StoreUserCreate,
  StoreUserUpdate,
} from '../api_types';
import { queryKeys } from '../query_keys';

const ALERTS_POLL_INTERVAL_MS = 60_000;
/** Export job polling interval (8.9.2 background job with polling). */
export const EXPORT_POLL_INTERVAL_MS = 2_000;

export function useAlertsQuery(enabled = true) {
  const endpoints = useEndpoints();
  return useQuery({
    queryKey: queryKeys.alerts,
    queryFn: ({ signal }) => endpoints.alerts.list(signal),
    refetchInterval: ALERTS_POLL_INTERVAL_MS,
    enabled,
  });
}

export function useMarkAlertReadMutation() {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) => endpoints.alerts.markRead(alertId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.alerts });
    },
  });
}

/** "Mark all as read" of the alerts page: every visible alert of the user (bell and list update together). */
export function useMarkAllAlertsReadMutation() {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => endpoints.alerts.markAllRead(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.alerts });
    },
  });
}

export function useAlertPreferencesQuery() {
  const endpoints = useEndpoints();
  return useQuery({
    queryKey: queryKeys.alertPreferences,
    queryFn: ({ signal }) => endpoints.alerts.preferences(signal),
  });
}

/** Where each alert channel delivers for the signed-in user (8.10: Telegram group, e-mail address). */
export function useAlertChannelsQuery() {
  const endpoints = useEndpoints();
  return useQuery({
    queryKey: queryKeys.alertChannels,
    queryFn: ({ signal }) => endpoints.alerts.channels(signal),
  });
}

export function useSaveAlertPreferencesMutation() {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preferencesUpdate: AlertPreferencesUpdate) =>
      endpoints.alerts.savePreferences(preferencesUpdate),
    onSuccess: (savedPreferences) => {
      queryClient.setQueryData(queryKeys.alertPreferences, savedPreferences);
    },
  });
}

export function useCreateExportMutation() {
  const endpoints = useEndpoints();
  return useMutation({
    mutationFn: (exportRequest: ExportRequest) => endpoints.exports.create(exportRequest),
  });
}

export function useExportJobQuery(exportId: string) {
  const endpoints = useEndpoints();
  return useQuery({
    queryKey: queryKeys.exportJob(exportId),
    queryFn: ({ signal }) => endpoints.exports.status(exportId, signal),
    refetchInterval: (query) => {
      const jobStatus = query.state.data?.status;
      return jobStatus === 'done' || jobStatus === 'failed' ? false : EXPORT_POLL_INTERVAL_MS;
    },
  });
}

export function useReportTemplatesQuery() {
  const endpoints = useEndpoints();
  return useQuery({
    queryKey: queryKeys.reportTemplates,
    queryFn: ({ signal }) => endpoints.reportTemplates.list(signal),
  });
}

export function useCreateReportTemplateMutation() {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateCreate: ReportTemplateCreate) => endpoints.reportTemplates.create(templateCreate),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.reportTemplates });
    },
  });
}

export function usePromosQuery() {
  const endpoints = useEndpoints();
  return useQuery({ queryKey: queryKeys.promos, queryFn: ({ signal }) => endpoints.calendar.promos(signal) });
}

export function useCreatePromoMutation() {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (promoCreate: PromoCreate) => endpoints.calendar.createPromo(promoCreate),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.promos });
    },
  });
}

export function useDeletePromoMutation() {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (promoId: string) => endpoints.calendar.deletePromo(promoId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.promos });
    },
  });
}

export function useHolidaysQuery(year: number) {
  const endpoints = useEndpoints();
  return useQuery({
    queryKey: queryKeys.holidays(year),
    queryFn: ({ signal }) => endpoints.calendar.holidays(year, signal),
    staleTime: 24 * 60 * 60_000,
  });
}

export function useStoreUsersQuery(enabled: boolean) {
  const endpoints = useEndpoints();
  return useQuery({
    queryKey: queryKeys.storeUsers,
    queryFn: ({ signal }) => endpoints.storeUsers.list(signal),
    enabled,
  });
}

export function useCreateStoreUserMutation() {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (storeUserCreate: StoreUserCreate) => endpoints.storeUsers.create(storeUserCreate),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeUsers });
    },
  });
}

/** Edit (name, stores) or block/unblock a store user. */
export function useUpdateStoreUserMutation() {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, storeUserUpdate }: { userId: string; storeUserUpdate: StoreUserUpdate }) =>
      endpoints.storeUsers.update(userId, storeUserUpdate),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeUsers });
    },
  });
}

/** A new temporary password for a store user, shown once. */
export function useResetStoreUserPasswordMutation() {
  const endpoints = useEndpoints();
  return useMutation({
    mutationFn: (userId: string) => endpoints.storeUsers.resetPassword(userId),
  });
}

export function useRemoveStoreUserMutation() {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => endpoints.storeUsers.remove(userId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeUsers });
    },
  });
}

export function useApiKeysQuery(enabled: boolean) {
  const endpoints = useEndpoints();
  return useQuery({
    queryKey: queryKeys.apiKeys,
    queryFn: ({ signal }) => endpoints.apiKeys.list(signal),
    enabled,
  });
}

export function useCreateApiKeyMutation() {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (apiKeyCreate: ApiKeyCreate) => endpoints.apiKeys.create(apiKeyCreate),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys });
    },
  });
}

export function useRevokeApiKeyMutation() {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (apiKeyId: string) => endpoints.apiKeys.revoke(apiKeyId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys });
    },
  });
}
